import React, { useState, useEffect } from 'react';
import {
  Shield,
  Search,
  CheckCircle,
  XCircle,
  Clock,
  Users,
  TrendingUp,
  FileText,
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const KYCManagement = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedData, setEditedData] = useState(null);

  const BACKEND_URL = '';

  useEffect(() => {
    fetchKYCData();
  }, [statusFilter]);

  const fetchKYCData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
      
      // Fetch submissions
      const submissionsRes = await fetch(
        `${BACKEND_URL}/api/kyc/admin/submissions?status=${statusFilter}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (submissionsRes.ok) {
        const submissionsData = await submissionsRes.json();
        setSubmissions(submissionsData.submissions || []);
      }
      
      // Fetch stats
      const statsRes = await fetch(`${BACKEND_URL}/api/kyc/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData.stats);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch KYC data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditKYCData = () => {
    setEditMode(true);
    setEditedData({...selectedSubmission.kyc_data});
  };

  const handleSaveEdits = async () => {
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('adminToken') || localStorage.getItem('secure_admin_token');
      const response = await fetch(`${BACKEND_URL}/api/kyc/admin/update-kyc-data`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: selectedSubmission.user_id,
          kyc_data: editedData
        })
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'KYC data updated successfully'
        });
        setEditMode(false);
        fetchKYCData();
        setShowModal(false);
        setSelectedSubmission(null);
      } else {
        throw new Error('Failed to update KYC data');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update KYC data',
        variant: 'destructive'
      });
    }
  };

  const handleUpdateStatus = async (userId, newStatus, message = '') => {
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('adminToken') || localStorage.getItem('secure_admin_token');
      const response = await fetch(`${BACKEND_URL}/api/kyc/admin/update-status`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: userId,
          kyc_status: newStatus,
          kyc_message: message
        })
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: `KYC status updated to ${newStatus}`
        });
        fetchKYCData();
        setShowModal(false);
        setSelectedSubmission(null);
        setEditMode(false);
      } else {
        throw new Error('Failed to update status');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update KYC status',
        variant: 'destructive'
      });
    }
  };

  const filteredSubmissions = submissions.filter(sub =>
    sub.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sub.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sub.kyc_data?.full_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const configs = {
      pending: { bg: 'bg-yellow-900/30', text: 'text-yellow-400', border: 'border-yellow-500/50', icon: Clock },
      approved: { bg: 'bg-green-900/30', text: 'text-green-400', border: 'border-green-500/50', icon: CheckCircle },
      rejected: { bg: 'bg-red-900/30', text: 'text-red-400', border: 'border-red-500/50', icon: XCircle }
    };
    
    const config = configs[status] || configs.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border ${config.bg} ${config.text} ${config.border} text-sm font-semibold`}>
        <Icon className="w-4 h-4" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const KYCDetailModal = () => {
    if (!selectedSubmission) return null;
    const kycData = selectedSubmission.kyc_data || {};
    
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-cyan-500/30 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-r from-slate-900 to-blue-900 border-b border-cyan-500/30 p-6 flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <Shield className="w-6 h-6 text-cyan-400" />
                KYC Verification Details
              </h2>
              <p className="text-cyan-400 mt-1">Review and approve/reject submission</p>
            </div>
            <button
              onClick={() => {
                setShowModal(false);
                setSelectedSubmission(null);
              }}
              className="text-gray-400 hover:text-white"
            >
              <XCircle className="w-6 h-6" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* User Info */}
            <div className="bg-slate-800/50 rounded-xl p-4">
              <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                <User className="w-5 h-5 text-cyan-400" />
                User Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-cyan-400 text-sm">Username</p>
                  <p className="text-white font-semibold">{selectedSubmission.username}</p>
                </div>
                <div>
                  <p className="text-cyan-400 text-sm">Email</p>
                  <p className="text-white font-semibold">{selectedSubmission.email}</p>
                </div>
                <div>
                  <p className="text-cyan-400 text-sm">Current Status</p>
                  <div className="mt-1">{getStatusBadge(selectedSubmission.kyc_status)}</div>
                </div>
                <div>
                  <p className="text-cyan-400 text-sm">Submitted At</p>
                  <p className="text-white font-semibold">
                    {selectedSubmission.kyc_submitted_at 
                      ? new Date(selectedSubmission.kyc_submitted_at).toLocaleString()
                      : 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {/* Personal Information */}
            <div className="bg-slate-800/50 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-cyan-400" />
                  Personal Information
                </h3>
                {!editMode && (
                  <button
                    onClick={handleEditKYCData}
                    className="px-3 py-1 text-xs bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 rounded-lg transition-colors"
                  >
                    Edit
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-cyan-400 text-sm flex items-center gap-1 mb-1">
                    <User className="w-4 h-4" /> Full Name
                  </p>
                  {editMode ? (
                    <input
                      type="text"
                      value={editedData?.full_name || ''}
                      onChange={(e) => setEditedData({...editedData, full_name: e.target.value})}
                      className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded text-white"
                    />
                  ) : (
                    <p className="text-white font-semibold">{kycData.full_name || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <p className="text-cyan-400 text-sm flex items-center gap-1 mb-1">
                    <Calendar className="w-4 h-4" /> Date of Birth
                  </p>
                  {editMode ? (
                    <input
                      type="date"
                      value={editedData?.dob || ''}
                      onChange={(e) => setEditedData({...editedData, dob: e.target.value})}
                      className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded text-white"
                    />
                  ) : (
                    <p className="text-white font-semibold">{kycData.dob || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <p className="text-cyan-400 text-sm flex items-center gap-1 mb-1">
                    <MapPin className="w-4 h-4" /> Country
                  </p>
                  {editMode ? (
                    <input
                      type="text"
                      value={editedData?.country || ''}
                      onChange={(e) => setEditedData({...editedData, country: e.target.value})}
                      className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded text-white"
                    />
                  ) : (
                    <p className="text-white font-semibold">{kycData.country || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <p className="text-cyan-400 text-sm flex items-center gap-1 mb-1">
                    <Phone className="w-4 h-4" /> Phone
                  </p>
                  {editMode ? (
                    <input
                      type="tel"
                      value={editedData?.phone || ''}
                      onChange={(e) => setEditedData({...editedData, phone: e.target.value})}
                      className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded text-white"
                    />
                  ) : (
                    <p className="text-white font-semibold">{kycData.phone || 'N/A'}</p>
                  )}
                </div>
              </div>
            </div>

            {/* ID Document */}
            <div className="bg-slate-800/50 rounded-xl p-4">
              <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                <FileText className="w-5 h-5 text-cyan-400" />
                ID Document
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-cyan-400 text-sm">ID Type</p>
                  <p className="text-white font-semibold">
                    {kycData.id_type === 'national_id' ? 'National ID' :
                     kycData.id_type === 'passport' ? 'Passport' :
                     kycData.id_type === 'drivers_license' ? "Driver's License" :
                     kycData.id_type || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-cyan-400 text-sm">ID Number</p>
                  <p className="text-white font-semibold font-mono">{kycData.id_number || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Address */}
            <div className="bg-slate-800/50 rounded-xl p-4">
              <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-cyan-400" />
                Address
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="text-cyan-400 text-sm">Street Address</p>
                  <p className="text-white font-semibold">{kycData.address || 'N/A'}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-cyan-400 text-sm">City</p>
                    <p className="text-white font-semibold">{kycData.city || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-cyan-400 text-sm">Postal Code</p>
                    <p className="text-white font-semibold">{kycData.postal_code || 'N/A'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Admin Message */}
            {selectedSubmission.kyc_message && (
              <div className="bg-blue-900/20 border border-blue-500/30 rounded-xl p-4">
                <p className="text-blue-400 text-sm font-semibold mb-1">Admin Message</p>
                <p className="text-blue-300">{selectedSubmission.kyc_message}</p>
              </div>
            )}

            {/* Action Buttons */}
            {editMode ? (
              <div className="flex gap-3">
                <button
                  onClick={handleSaveEdits}
                  className="flex-1 px-6 py-3 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-semibold flex items-center justify-center gap-2"
                >
                  <CheckCircle className="w-5 h-5" />
                  Save Changes
                </button>
                <button
                  onClick={() => {
                    setEditMode(false);
                    setEditedData(null);
                  }}
                  className="flex-1 px-6 py-3 rounded-lg bg-gray-500 hover:bg-gray-600 text-white font-semibold flex items-center justify-center gap-2"
                >
                  <XCircle className="w-5 h-5" />
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex gap-3">
                <button
                  onClick={() => handleUpdateStatus(selectedSubmission.user_id, 'approved', 'KYC verification approved. All platform features unlocked.')}
                  className="flex-1 px-6 py-3 rounded-lg bg-green-500 hover:bg-green-600 text-white font-semibold flex items-center justify-center gap-2"
                >
                  <CheckCircle className="w-5 h-5" />
                  Approve
                </button>
                <button
                  onClick={() => {
                    const message = prompt('Enter rejection reason:');
                    if (message) {
                      handleUpdateStatus(selectedSubmission.user_id, 'rejected', message);
                    }
                  }}
                  className="flex-1 px-6 py-3 rounded-lg bg-red-500 hover:bg-red-600 text-white font-semibold flex items-center justify-center gap-2"
                >
                  <XCircle className="w-5 h-5" />
                  Reject
                </button>
                <button
                  onClick={() => handleUpdateStatus(selectedSubmission.user_id, 'pending', 'KYC verification under review.')}
                  className="flex-1 px-6 py-3 rounded-lg bg-yellow-500 hover:bg-yellow-600 text-white font-semibold flex items-center justify-center gap-2"
                >
                  <Clock className="w-5 h-5" />
                  Set Pending
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <Shield className="w-8 h-8 text-cyan-400" />
            KYC Management
          </h1>
          <p className="text-cyan-400">Review and manage user identity verifications</p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-gradient-to-br from-blue-900/50 to-blue-800/30 border border-cyan-500/30 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-cyan-400 text-sm">Total Users</p>
                  <p className="text-2xl font-bold text-white">{stats.total_users}</p>
                </div>
                <Users className="w-10 h-10 text-cyan-400 opacity-50" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-yellow-900/50 to-yellow-800/30 border border-yellow-500/30 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-yellow-400 text-sm">Pending</p>
                  <p className="text-2xl font-bold text-white">{stats.pending}</p>
                </div>
                <Clock className="w-10 h-10 text-yellow-400 opacity-50" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-900/50 to-green-800/30 border border-green-500/30 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-400 text-sm">Approved</p>
                  <p className="text-2xl font-bold text-white">{stats.approved}</p>
                </div>
                <CheckCircle className="w-10 h-10 text-green-400 opacity-50" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-900/50 to-purple-800/30 border border-purple-500/30 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-400 text-sm">Verification Rate</p>
                  <p className="text-2xl font-bold text-white">{stats.verification_rate}%</p>
                </div>
                <TrendingUp className="w-10 h-10 text-purple-400 opacity-50" />
              </div>
            </div>
          </div>
        )}

        {/* Filters & Search */}
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-cyan-500/30 rounded-xl p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-cyan-400" />
              <input
                type="text"
                placeholder="Search by username, email, or name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-slate-800 border border-cyan-500/30 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400"
              />
            </div>

            {/* Status Filter */}
            <div className="flex gap-2">
              {['all', 'pending', 'approved', 'rejected'].map(status => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-4 py-3 rounded-lg font-semibold transition-all ${
                    statusFilter === status
                      ? 'bg-cyan-500 text-white'
                      : 'bg-slate-800 text-cyan-400 hover:bg-slate-700'
                  }`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>

            {/* Refresh Button */}
            <button
              onClick={fetchKYCData}
              disabled={loading}
              className="px-4 py-3 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-semibold flex items-center gap-2 disabled:opacity-50"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Submissions Table */}
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-cyan-500/30 rounded-xl overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <RefreshCw className="w-12 h-12 text-cyan-400 animate-spin mx-auto mb-4" />
              <p className="text-cyan-400">Loading KYC submissions...</p>
            </div>
          ) : filteredSubmissions.length === 0 ? (
            <div className="p-12 text-center">
              <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-400">No KYC submissions found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800/50">
                  <tr>
                    <th className="text-left p-4 text-cyan-400 font-semibold">User</th>
                    <th className="text-left p-4 text-cyan-400 font-semibold">Email</th>
                    <th className="text-left p-4 text-cyan-400 font-semibold">Full Name</th>
                    <th className="text-left p-4 text-cyan-400 font-semibold">Submitted</th>
                    <th className="text-left p-4 text-cyan-400 font-semibold">Status</th>
                    <th className="text-left p-4 text-cyan-400 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSubmissions.map((submission, index) => (
                    <tr
                      key={submission.user_id}
                      className={`border-t border-cyan-500/10 hover:bg-slate-800/50 ${
                        index % 2 === 0 ? 'bg-slate-900/30' : ''
                      }`}
                    >
                      <td className="p-4 text-white font-semibold">{submission.username}</td>
                      <td className="p-4 text-gray-300">{submission.email}</td>
                      <td className="p-4 text-gray-300">{submission.kyc_data?.full_name || 'N/A'}</td>
                      <td className="p-4 text-gray-400 text-sm">
                        {submission.kyc_submitted_at 
                          ? new Date(submission.kyc_submitted_at).toLocaleDateString()
                          : 'N/A'}
                      </td>
                      <td className="p-4">{getStatusBadge(submission.kyc_status)}</td>
                      <td className="p-4">
                        <button
                          onClick={() => {
                            setSelectedSubmission(submission);
                            setShowModal(true);
                          }}
                          className="px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white font-semibold text-sm"
                        >
                          Review
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && <KYCDetailModal />}
    </div>
  );
};

export default KYCManagement;
